import React, { useState } from 'react';
import { motion, Reorder } from 'framer-motion';
import QuestionCard from './QuestionCard';
import { getThemeColors } from '../../../utils/themeConfig';

const FormCanvas = ({ 
  formTitle = '',
  formDescription = '',
  questions = [],
  selectedQuestionId = null,
  formSettings = {},
  onFormUpdate = () => {},
  onQuestionSelect = () => {},
  onQuestionUpdate = () => {},
  onQuestionDelete = () => {},
  onQuestionDuplicate = () => {},
  onAddQuestion = () => {},
  onReorderQuestions = () => {}
}) => {
  // Get theme colors with fallback
  let theme;
  try {
    theme = getThemeColors(formSettings?.theme || 'white');
  } catch (error) {
    console.error('Error loading theme:', error);
    theme = {
      primary: '#0066FF',
      background: '#F9FAFB',
      cardBg: '#FFFFFF',
      text: '#111827',
      textSecondary: '#6B7280',
      textMuted: '#9CA3AF',
      border: '#E5E7EB',
      inputBg: '#FFFFFF'
    };
  }

  const handleReorder = (newOrder) => {
    if (!Array.isArray(questions) || !Array.isArray(newOrder)) return;
    
    newOrder.forEach((question, newIndex) => {
      const oldIndex = questions.findIndex(q => q?.id === question?.id);
      if (oldIndex !== -1 && oldIndex !== newIndex) {
        onReorderQuestions(oldIndex, newIndex);
      }
    });
  };

  // Handle click on white space to unselect questions
  const handleCanvasClick = (e) => {
    // Check if the click is on the main container itself (white space)
    // and not on any question card or interactive element
    if (e.target === e.currentTarget) {
      onQuestionSelect(null);
    }
  };

  // Safety check - ensure questions is always an array
  const safeQuestions = Array.isArray(questions) ? questions : [];

  return (
    <main 
      className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 transition-colors duration-300"
      style={{ backgroundColor: theme.background }}
      onClick={handleCanvasClick} // Add click handler here
    >
      <div className="max-w-3xl mx-auto">
        {/* Form Header Section */}
        <div 
          className="rounded-xl border p-6 sm:p-8 mb-6 transition-all duration-300"
          style={{ 
            backgroundColor: theme.cardBg,
            borderColor: theme.border
          }}
        >
          <input
            type="text"
            placeholder="Form Title"
            value={formTitle}
            onChange={(e) => onFormUpdate('title', e.target.value)}
            className="text-2xl sm:text-3xl font-bold w-full bg-transparent border-none outline-none placeholder-gray-300 mb-3 focus:ring-2 rounded px-2 py-1 transition-colors"
            style={{ 
              color: theme.text
            }}
          />
          <textarea
            placeholder="Add a description to help your audience understand what this form is about..."
            value={formDescription}
            onChange={(e) => onFormUpdate('description', e.target.value)}
            className="w-full bg-transparent border-none outline-none placeholder-gray-400 resize-none focus:ring-2 rounded px-2 py-1 transition-colors"
            style={{ 
              color: theme.textSecondary
            }}
            rows="2"
          />
        </div>

        {/* Questions Area with Drag & Drop */}
        {safeQuestions.length > 0 ? (
          <Reorder.Group 
            axis="y" 
            values={safeQuestions} 
            onReorder={handleReorder}
            className="space-y-4"
          >
            {safeQuestions.map((question, index) => (
              <Reorder.Item key={question.id} value={question}>
                <QuestionCard
                  question={question}
                  index={index}
                  isSelected={selectedQuestionId === question.id}
                  onSelect={() => onQuestionSelect(question.id)}
                  onUpdate={onQuestionUpdate}
                  onDelete={onQuestionDelete}
                  onDuplicate={onQuestionDuplicate}
                  isDragging={false} // Remove drag state tracking
                  theme={theme}
                />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        ) : (
          /* Empty State */
          <div 
            className="rounded-xl border-2 border-dashed p-12 text-center transition-all duration-300"
            style={{ 
              backgroundColor: theme.cardBg,
              borderColor: theme.border
            }}
          >
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${theme.primary}1A` }}
            >
              <span className="text-3xl" style={{ color: theme.primary }}>+</span>
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
              No questions yet
            </h3>
            <p className="mb-6 max-w-sm mx-auto" style={{ color: theme.textSecondary }}>
              Click "Add Question" to start building your form. You can add text, multiple choice, ratings, and more.
            </p>
            <button 
              onClick={onAddQuestion}
              className="px-6 py-3 text-white rounded-lg hover:opacity-90 transition-all font-medium"
              style={{ backgroundColor: theme.primary }}
            >
              Add Your First Question
            </button>
          </div>
        )}

        {/* Add Question Button (always visible) */}
        {safeQuestions.length > 0 && (
          <button 
            onClick={onAddQuestion}
            className="w-full mt-6 py-4 border-2 border-dashed rounded-xl hover:opacity-80 transition-all flex items-center justify-center gap-2 font-medium"
            style={{ 
              borderColor: theme.border,
              color: theme.textMuted,
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.color = theme.primary;
              e.currentTarget.style.backgroundColor = `${theme.primary}0D`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.border;
              e.currentTarget.style.color = theme.textMuted;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span className="text-xl">+</span>
            <span>Add Question</span>
          </button>
        )}
      </div>
    </main>
  );
};

export default FormCanvas;